import { Component } from "react";

/**
 * Catches render/lifecycle errors in its subtree so a single failing
 * component degrades gracefully instead of unmounting the whole React
 * tree (which shows the user a blank page).
 *
 * @param {object} props
 * @param {import("react").ReactNode} props.children - Subtree to protect.
 * @param {import("react").ReactNode | ((error: Error) => import("react").ReactNode)} [props.fallback]
 *   - Rendered when a descendant throws. Element or `(error) => element`.
 * @param {(error: Error, info: object) => void} [props.onError] - Optional side-effect on catch.
 */
class ErrorBoundary extends Component {
    state = { error: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error("ErrorBoundary caught an error:", error, info);
        this.props.onError?.(error, info);
    }

    render() {
        const { error } = this.state;
        if (error !== null) {
            const { fallback } = this.props;
            return typeof fallback === "function" ? fallback(error) : (fallback ?? null);
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
